/**
 * Utility for extracting text content from LLM prompt structures
 * Handles various formats: strings, arrays, objects with text/content properties
 */

/**
 * Extracts text content from various prompt formats
 * Handles:
 * - Plain strings
 * - Arrays of strings or content blocks
 * - Objects with text or content properties
 * - Nested structures
 *
 * @param content - The prompt content in any supported format
 * @returns Extracted text as a single string
 *
 * @example
 * // Plain string
 * extractPromptText("Hello") // "Hello"
 *
 * @example
 * // Array of strings
 * extractPromptText(["Hello", "World"]) // "Hello\n\n---\n\nWorld"
 *
 * @example
 * // Array of content blocks
 * extractPromptText([{ text: "Hello" }, { text: "World" }]) // "Hello\n\n---\n\nWorld"
 *
 * @example
 * // Object with text property
 * extractPromptText({ text: "Hello" }) // "Hello"
 */
export function extractPromptText(content: unknown): string {
	// Handle null/undefined
	if (content === null || content === undefined) {
		return '';
	}

	// Handle plain strings
	if (typeof content === 'string') {
		return content;
	}

	// Handle arrays (of strings or content blocks)
	if (Array.isArray(content)) {
		return content
			.map((item: unknown) => extractContentBlockText(item))
			.filter((text) => text.length > 0)
			.join('\n\n---\n\n');
	}

	// Handle objects with text or content properties
	if (content && typeof content === 'object') {
		return extractObjectText(content as Record<string, unknown>);
	}

	// Fallback: convert to string
	return String(content);
}

/**
 * Extracts text from a single content block
 * Content blocks can be strings, objects with text property, or objects with content property
 */
function extractContentBlockText(item: unknown): string {
	if (typeof item === 'string') {
		return item;
	}

	if (item && typeof item === 'object') {
		const obj = item as Record<string, unknown>;

		// Check for 'text' property (common in Anthropic message format)
		if (typeof obj.text === 'string') {
			return obj.text;
		}

		// Check for 'content' property (alternative format)
		if (typeof obj.content === 'string') {
			return obj.content;
		}

		// Fallback to JSON stringification for complex objects
		return JSON.stringify(item, null, 2);
	}

	return String(item);
}

/**
 * Extracts text from an object
 * Checks for common text properties before falling back to JSON
 */
function extractObjectText(obj: Record<string, unknown>): string {
	// Check for 'text' property
	if (typeof obj.text === 'string') {
		return obj.text;
	}

	// Check for 'content' property
	if (typeof obj.content === 'string') {
		return obj.content;
	}

	// Fallback to JSON stringification
	return JSON.stringify(obj, null, 2);
}

/**
 * Truncates text to a maximum length with ellipsis
 * Useful for logging previews
 *
 * @param text - The text to truncate
 * @param maxLength - Maximum length (default: 100)
 * @returns Truncated text with ellipsis if needed
 */
export function truncateForPreview(text: string, maxLength: number = 100): string {
	if (text.length <= maxLength) {
		return text;
	}
	return text.substring(0, maxLength) + '...';
}
